export type VideoSyncStatus = 'pending' | 'synced' | 'failed' | 'alert_sent';
export type IngestSource = 'inhouse';
export type ServiceName = 'odk' | 'odc' | 'amasian';

export interface UrlMismatch {
  type: 'sources' | 'manifests';
  preset: string;
  expected: string | null;
  actual: string | null;
}

export interface MetadataMismatch {
  type: 'metadata';
  field: string;
  ucms_value: string | null;
  odk_value: string | null;
}

export interface VideoSyncTracker {
  id: number;
  content_id: number;
  content_title: string;
  content_slug?: string;
  content_status?: string;
  video_id: number;
  video_filename: string;
  video_file_url?: string;
  video_status?: string;
  season_id?: number;
  season_title?: string;
  ingest_source: IngestSource;
  service_name: ServiceName;
  status: VideoSyncStatus;
  expected_check_time: string;
  check_count: number;
  last_checked_at: string | null;
  odk_episode_id: number | null;
  odk_video_status: string | null;
  expected_presets: string[];
  found_presets: string[];
  missing_presets: string[];
  // URL comparison related fields
  expected_sources?: Record<string, string>;
  expected_manifests?: Record<string, string>;
  actual_sources?: Record<string, string>;
  actual_manifests?: Record<string, string>;
  url_mismatches?: UrlMismatch[];
  metadata_mismatches?: MetadataMismatch[];
  error_message: string | null;
  created: string;
  modified: string;
}

export interface VideoSyncStats {
  total: number;
  pending: number;
  synced: number;
  failed: number;
  alert_sent: number;
  avg_sync_time_minutes: number | null;
  today: {
    total: number;
    pending: number;
    synced: number;
    failed: number;
    alert_sent: number;
  };
  by_source: Record<string, number>;
  by_service: Record<string, number>;
}

export interface TestSyncRequest {
  content_id: number;
  expected_presets?: string[];
  save_result?: boolean; // Save result to existing tracker (default: false)
}

export interface TestSyncByTrackerRequest {
  tracker_id: number;
  save_result?: boolean; // Save result to tracker (default: false)
}

export interface TestSyncResponse {
  content_id: number;
  content_title: string;
  synced: boolean;
  episode_id: number | null;
  episode_title: string | null;
  video_status: string | null;
  expected_presets: string[];
  found_presets: string[];
  missing_presets: string[];
  // URL comparison related fields
  expected_sources?: Record<string, string>;
  expected_manifests?: Record<string, string>;
  actual_sources?: Record<string, string>;
  actual_manifests?: Record<string, string>;
  url_mismatches?: UrlMismatch[];
  metadata_mismatches?: MetadataMismatch[];
  reason: string | null;
  existing_tracker_id?: number | null;
  tracker_id?: number;
  tracker_status?: string;
  tracker_check_count?: number;
  saved?: boolean; // Whether result was saved when save_result=true and existing tracker exists
}

export interface BulkRetryRequest {
  ids: number[];
}

export interface BulkRetryResponse {
  updated_count: number;
  message: string;
}

export const VideoSyncStatusDisplay: Record<VideoSyncStatus, string> = {
  pending: 'Pending',
  synced: 'Synced',
  failed: 'Failed',
  alert_sent: 'Alert Sent',
};

export const VideoSyncStatusColor: Record<VideoSyncStatus, string> = {
  pending: 'gold',
  synced: 'green',
  failed: 'red',
  alert_sent: 'orange',
};

export const VideoSyncStatusIcon: Record<VideoSyncStatus, string> = {
  pending: '⏳',
  synced: '✅',
  failed: '❌',
  alert_sent: '🔔',
};

export const IngestSourceDisplay: Record<IngestSource, string> = {
  inhouse: 'Inhouse',
};

export const ServiceNameDisplay: Record<ServiceName, string> = {
  odk: 'ODK',
  odc: 'ODC',
  amasian: 'Amasian',
};
